import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import NumberField from 'commons/components/NumberField';

const getHeaderClass = (classes, headCell) => {
  if (headCell.kardexHeader) return classes.kardexHeader;
  if (headCell.zcotHeader) return classes.zcotHeader;
  if (headCell.zcorHeader) return classes.zcorHeader;
  return '';
};

const EnhancedTableHead = React.memo((customProps) => {
  const { classes, headCells, onRequestSort, orderX, orderByX } = customProps;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };
  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <Tooltip key={headCell.id} title={headCell.tooltip} placement="top-start">
            <TableCell
              align={headCell.numeric ? 'right' : 'left'}
              // padding={headCell.disablePadding ? 'none' : 'normal'}
              sortDirection={orderByX === headCell.id ? orderX : false}
              className={getHeaderClass(classes, headCell)}>
              <TableSortLabel
                active={orderByX === headCell.id}
                direction={orderByX === headCell.id ? orderX : 'asc'}
                // direction="desc"
                onClick={createSortHandler(headCell.id)}>
                {headCell.label}
                {orderByX === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {orderX === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
          </Tooltip>
        ))}
      </TableRow>
    </TableHead>
  );
});

const FormulaResultsTable = function (props) {
  const {
    controls,
    actions,
    classes,
    convertKardexValue,
    convertZcotValue,
    toUsdZcotValue,
    currencyCode,
    readOnly = false,
  } = props;

  const [order, setOrder] = React.useState('desc');
  const [orderBy, setOrderBy] = React.useState('incidencePercent');
  const materialsList = useMemo(() => Object.values(controls.materials), [controls.materials]);
  const headCells = [
    { id: 'component', numeric: false, label: 'Código mat.', tooltip: '' },
    { id: 'name', numeric: false, label: 'Nombre', tooltip: '' },
    {
      id: 'subQuantity',
      numeric: false,
      label: 'Aporte',
      tooltip: 'Aporte en kg del material asociado a cantidad base',
    },
    { id: 'base_unit', numeric: false, label: 'UMB', tooltip: 'Unidad de medida base' },
    {
      id: 'unit',
      numeric: false,
      label: 'UM',
      tooltip: 'Unidad de medida del material en la formula',
    },
    {
      id: 'quant_base',
      numeric: false,
      label: 'Cant. base',
      tooltip: 'Cantidad que se encuentra formulada la premezcla',
    },
    {
      id: 'costDollar',
      numeric: false,
      label: 'Kardex unit.',
      kardexHeader: true,
      tooltip: 'Costo de 1 unidad actual en SAP',
    },
    {
      id: 'costPerFormulaDollar',
      numeric: false,
      label: 'Kardex',
      kardexHeader: true,
      tooltip: 'Costo unitario kardex por el aporte dividido la cantidad base',
    },
    {
      id: 'zcot',
      numeric: false,
      label: 'ZCOT unit.',
      zcotHeader: true,
      tooltip: 'Costo proyectado',
    },
    {
      id: 'zcotPerFormula',
      numeric: false,
      label: 'ZCOT',
      zcotHeader: true,
      tooltip: 'Costo de 1 unidad proyectado',
    },
    {
      id: 'zcorUnit',
      numeric: false,
      label: 'ZCOR unit.',
      zcorHeader: true,
      tooltip: 'Costo corregido unitario (editable)',
    },
    {
      id: 'zcorPerFormula',
      numeric: false,
      label: 'ZCOR',
      zcorHeader: true,
      tooltip: 'Costo corregido por el aporte',
    },
    {
      id: 'incidencePercent',
      numeric: false,
      label: '% Incidencia',
      kardexHeader: true,
      tooltip: 'Incidencia porcentual de costo Kardex sobre el costo Kardex total',
    },
  ];
  const getLocaleStringParams = (code) => {
    if (code === 'CLP') {
      return { maximumFractionDigits: 0, minimumFractionDigits: 0 };
    }
    return { maximumFractionDigits: 2, minimumFractionDigits: 2 };
  };

  const lsParams = getLocaleStringParams(currencyCode);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  function descendingComparator(a, b, orderByX) {
    const aValue = a[orderByX];
    const bValue = b[orderByX];
    const aIsNumber = !Number.isNaN(parseFloat(aValue)) && Number.isFinite(aValue);
    const bIsNumber = !Number.isNaN(parseFloat(bValue)) && Number.isFinite(bValue);
    if (aIsNumber && bIsNumber) {
      return parseFloat(bValue) - parseFloat(aValue);
    }
    const aParsed = (aValue || '').toString().toLowerCase();
    const bParsed = (bValue || '').toString().toLowerCase();
    if (aParsed < bParsed) {
      return 1;
    }
    if (aParsed > bParsed) {
      return -1;
    }
    return 0;
  }

  function getComparator(orderX, orderByX) {
    return orderX === 'desc'
      ? (a, b) => descendingComparator(a, b, orderByX)
      : (a, b) => -descendingComparator(a, b, orderByX);
  }

  // This method is created for cross-browser compatibility, if you don't
  // need to support IE11, you can use Array.prototype.sort() directly
  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const orderX = comparator(a[0], b[0]);
      if (orderX !== 0) {
        return orderX;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  const getComparatorSymbol = (kardex, zcot) => {
    const parsedKardex = kardex?.toFixed(2) || 0;
    const parsedZcot = zcot?.toFixed(2) || 0;
    if (Number(parsedKardex) > Number(parsedZcot)) return '↓';
    if (Number(parsedKardex) < Number(parsedZcot)) return '↑';
    return '';
  };

  const getComparatorClass = (kardex, zcot) => {
    const parsedKardex = kardex?.toFixed(2) || 0;
    const parsedZcot = zcot?.toFixed(2) || 0;
    if (Number(parsedKardex) > Number(parsedZcot)) return classes.textGreen;
    if (Number(parsedKardex) < Number(parsedZcot)) return classes.textRed;
    return '';
  };

  const getComparatorClassOrSymbol = (type, kardexValue, zcotValue) => {
    const kardex = convertKardexValue(kardexValue || 0);
    const zcot = convertZcotValue(zcotValue || 0);
    if (type === 'class') {
      return getComparatorClass(kardex, zcot);
    }
    if (type === 'symbol') {
      return getComparatorSymbol(kardex, zcot);
    }
    return '';
  };

  // Una línea se considera "corregida" cuando su ZCOR unit. quedó distinto del ZCOT unit. vigente
  // (redondeado a 4 decimales para no marcar diferencias por ruido de punto flotante).
  const isRowCorrected = (row) => {
    const zcorUnit = Math.round(Number(row?.zcorUnit || 0) * 10000);
    const zcot = Math.round(Number(row?.zcot || 0) * 10000);
    return zcorUnit !== zcot;
  };

  const handleChangeZcor = useCallback(
    (component, e) => {
      const { value } = e.target;
      const zcorUnit = toUsdZcotValue(value === '' ? 0 : Number(value));
      const newMaterials = controls.materials.map((m) => {
        if (m.component !== component) return m;
        return { ...m, zcorUnit, zcorPerFormula: m.quantityQuotient * zcorUnit };
      });
      const totalZcor = newMaterials.reduce((acc, m) => acc + m.zcorPerFormula, 0);
      actions.changeControls({
        materials: newMaterials,
        totals: {
          ...controls.totals,
          totalZcor,
          totalProjectedCostZcor: totalZcor + (controls.totals?.totalVariableCosts || 0),
        },
        zcorHasChanges: true,
      });
    },
    [controls.materials, controls.totals, actions, toUsdZcotValue],
  );

  return (
    <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
      <Table
        sx={{ minWidth: 650 }}
        stickyHeader
        size="small"
        aria-label="detalle del cálculo de la fórmula">
        <EnhancedTableHead
          headCells={headCells}
          classes={classes}
          orderX={order}
          orderByX={orderBy}
          onRequestSort={handleRequestSort}
        />
        <TableBody>
          {stableSort(materialsList, getComparator(order, orderBy)).map((row) => {
            const corrected = isRowCorrected(row);
            return (
              <TableRow
                className={classes.zebra}
                key={row.component}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  ...(corrected && { boxShadow: 'inset 3px 0 0 0 #ed6c02' }),
                }}>
                <TableCell align="left">{row?.component}</TableCell>
                <TableCell align="left">{row?.name}</TableCell>
                <TableCell align="right">
                  {row?.subQuantity?.toLocaleString('es-CL', lsParams)}
                </TableCell>
                <TableCell align="right">{row?.base_unit}</TableCell>
                <TableCell align="right">{row?.unit}</TableCell>
                <TableCell align="right">{row?.quant_base}</TableCell>
                {/* KARDEX unit */}
                <TableCell align="right" className={classes.zebraKardex}>
                  {convertKardexValue(row?.costDollar || 0).toLocaleString('es-CL', lsParams)}
                </TableCell>
                {/* KARDEX */}
                <TableCell align="right" className={classes.zebraKardex}>
                  {convertKardexValue(row?.costPerFormulaDollar || 0).toLocaleString(
                    'es-CL',
                    lsParams,
                  )}
                </TableCell>
                {/* ZCOT unit. */}
                <TableCell
                  align="right"
                  className={getComparatorClassOrSymbol('class', row?.costDollar, row?.zcot)}>
                  {`${getComparatorClassOrSymbol(
                    'symbol',
                    row?.costDollar,
                    row?.zcot,
                  )} ${convertZcotValue(row?.zcot).toLocaleString('es-CL', lsParams)}`}
                </TableCell>
                {/* ZCOT */}
                <TableCell
                  align="right"
                  className={getComparatorClassOrSymbol(
                    'class',
                    row?.costPerFormulaDollar,
                    row?.zcotPerFormula,
                  )}>
                  {`${getComparatorClassOrSymbol(
                    'symbol',
                    row?.costPerFormulaDollar,
                    row?.zcotPerFormula,
                  )} ${convertZcotValue(row?.zcotPerFormula).toLocaleString('es-CL', lsParams)}`}
                </TableCell>
                {/* ZCOR unit. (editable, salvo en modo solo lectura) */}
                <TableCell align="right">
                  <Box display="flex" alignItems="center" justifyContent="flex-end">
                    {corrected && (
                      <Tooltip title="ZCOR corregido respecto al ZCOT vigente">
                        <Typography
                          component="span"
                          variant="caption"
                          color="warning.main"
                          fontWeight="bold"
                          mr="6px">
                          ●
                        </Typography>
                      </Tooltip>
                    )}
                    {readOnly ? (
                      convertZcotValue(row?.zcorUnit || 0).toLocaleString('es-CL', lsParams)
                    ) : (
                      <NumberField
                        id="zcorUnit"
                        label=""
                        variant="standard"
                        size="small"
                        value={convertZcotValue(row?.zcorUnit || 0)}
                        onChange={(e) => handleChangeZcor(row.component, e)}
                        sx={{ width: '90px' }}
                        maxDecimals={4}
                      />
                    )}
                  </Box>
                </TableCell>
                {/* ZCOR */}
                <TableCell
                  align="right"
                  className={getComparatorClassOrSymbol(
                    'class',
                    row?.costPerFormulaDollar,
                    row?.zcorPerFormula,
                  )}>
                  {`${getComparatorClassOrSymbol(
                    'symbol',
                    row?.costPerFormulaDollar,
                    row?.zcorPerFormula,
                  )} ${convertZcotValue(row?.zcorPerFormula).toLocaleString('es-CL', lsParams)}`}
                </TableCell>
                <TableCell align="right" className={classes.zebraKardex}>
                  {row?.incidencePercent?.toFixed(2)}%
                </TableCell>
              </TableRow>
            );
          })}
          <TableRow className={classes.stickyRow} sx={{ bottom: '103px' }}>
            <TableCell rowSpan={5} />
            <TableCell colSpan={1}>Total</TableCell>
            <TableCell align="right">
              {controls.totals?.totalQuantity?.toLocaleString('es-CL', lsParams)}
            </TableCell>
            <TableCell align="right" />
            <TableCell align="right" />
            <TableCell align="right" />
            <TableCell align="right" />
            <TableCell align="right">
              {convertKardexValue(controls.totals?.totalCostDolar || 0).toLocaleString(
                'es-CL',
                lsParams,
              )}
            </TableCell>
            <TableCell align="right" />
            <TableCell align="right">
              {convertZcotValue(controls.totals?.totalZcot).toLocaleString('es-CL', lsParams)}
            </TableCell>
            <TableCell align="right" />
            <TableCell align="right">
              {convertZcotValue(controls.totals?.totalZcor).toLocaleString('es-CL', lsParams)}
            </TableCell>
            <TableCell align="right" />
          </TableRow>
          <TableRow className={classes.stickyRow} sx={{ bottom: '70px' }}>
            <TableCell colSpan={5}>
              Costos variables{' '}
              {!readOnly && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => actions.changeModals('variableCosts')}>
                  Editar costos
                </Button>
              )}
            </TableCell>
            <TableCell align="right">-</TableCell>
            <TableCell align="right">-</TableCell>
            <TableCell align="right">-</TableCell>
            <TableCell align="right">
              {convertKardexValue(controls.totals?.totalVariableCosts).toLocaleString(
                'es-CL',
                lsParams,
              )}
            </TableCell>
            <TableCell align="right">-</TableCell>
            <TableCell align="right">
              {convertKardexValue(controls.totals?.totalVariableCosts).toLocaleString(
                'es-CL',
                lsParams,
              )}
            </TableCell>
            <TableCell align="right">-</TableCell>
          </TableRow>
          <TableRow className={classes.stickyRow} sx={{ bottom: '37px' }}>
            <TableCell colSpan={5}>Costo estándar</TableCell>
            <TableCell align="right">-</TableCell>
            <TableCell align="right">
              {convertKardexValue(controls.totals?.kardexDolar).toLocaleString('es-CL', lsParams)}
            </TableCell>
            <TableCell align="right">-</TableCell>
            <TableCell align="right">-</TableCell>
            <TableCell align="right">-</TableCell>
            <TableCell align="right">-</TableCell>
            <TableCell align="right">-</TableCell>
          </TableRow>
          <TableRow className={classes.stickyRow} sx={{ bottom: '0px' }}>
            <TableCell colSpan={5}>Costo proyectado (Total ZCOT + Costos variables)</TableCell>
            <TableCell align="right">-</TableCell>
            <TableCell align="right">-</TableCell>
            <TableCell align="right">-</TableCell>
            <TableCell align="right">
              <Typography fontWeight="bold">
                {convertZcotValue(controls.totals?.totalProjectedCost).toLocaleString(
                  'es-CL',
                  lsParams,
                )}
              </Typography>
            </TableCell>
            <TableCell align="right">-</TableCell>
            <TableCell align="right">
              <Typography fontWeight="bold">
                {convertZcotValue(controls.totals?.totalProjectedCostZcor).toLocaleString(
                  'es-CL',
                  lsParams,
                )}
              </Typography>
            </TableCell>
            <TableCell align="right">-</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FormulaResultsTable;
